/**
 * Contact person — the third step of a tiffin order.
 *
 * Unlike a donor, a customer must give a name and a mobile number: the
 * kitchen calls about the order and the rider rings when they are outside.
 * The email is optional.
 */

import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { validateTiffinCustomer } from '@mfa/domain';
import { space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ArrowIcon } from '../../components/common/Icon';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { TIFFIN_STEPS } from '../../components/donation/CheckoutStepper';
import { Field } from '../../components/forms/Field';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { TiffinTotalFooter, useTiffinCheckout } from './useTiffinCheckout';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function TiffinContactScreen({ navigation }: DonorScreenProps<'TiffinContact'>) {
  const t = useT();
  const checkout = useTiffinCheckout(navigation);
  const { draft } = checkout;
  const { customer } = draft;
  const [touched, setTouched] = useState(false);
  const errors = validateTiffinCustomer(customer);
  const show = (message?: string) => (touched && message ? t(message) : null);

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-tiffin-contact"
      subHeader={
        <CheckoutHeader
          current={2}
          steps={TIFFIN_STEPS}
          title={t('Contact Person')}
          subtitle={t('Who the kitchen and the rider call')}
        />
      }
      pinnedFooter={
        <TiffinTotalFooter checkout={checkout}>
          <Button
            label={t('Continue')}
            onPress={() => {
              setTouched(true);
              if (Object.keys(errors).length === 0) navigation.navigate('TiffinDelivery');
            }}
            trailingIcon={<ArrowIcon />}
            testID="cta-tiffin-contact-continue"
          />
        </TiffinTotalFooter>
      }
    >
      <Card title={t('Your details')} testID="tiffin-contact">
        <View style={styles.fields}>
          <Field
            label={t('Full Name')}
            required
            icon="person"
            value={customer.fullName}
            onChangeText={(value) => draft.setCustomer({ fullName: value })}
            autoComplete="name"
            error={show(errors.fullName)}
            testID="field-tiffin-name"
          />
          <Field
            label={t('Mobile Number')}
            required
            icon="mobile"
            value={customer.mobileNumber}
            onChangeText={(value) => draft.setCustomer({ mobileNumber: value })}
            placeholder="03XX XXXXXXX"
            keyboardType="phone-pad"
            autoComplete="tel"
            hint={t('The kitchen or rider calls this number.')}
            error={show(errors.mobileNumber)}
            testID="field-tiffin-mobile"
          />
          <Field
            label={t('Email Address')}
            icon="email"
            value={customer.email ?? ''}
            onChangeText={(value) => draft.setCustomer({ email: value })}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            error={show(errors.email)}
            testID="field-tiffin-email"
          />
        </View>
      </Card>
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  fields: {
    gap: space.md,
  },
});
